import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import {
  UploadSimple, FileArrowUp, Folder, X, CheckCircle, Warning, Spinner, CalendarBlank,
  Sparkle, Buildings,
} from "@phosphor-icons/react";

const stripExt = (name) => name.replace(/\.[^.]+$/, "");

export default function UploadPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedDept = searchParams.get("dept") || "";
  const folderRef = useRef(null);
  const [cats, setCats] = useState([]);
  const [mode, setMode] = useState("single"); // "single" | "bulk"

  // Enable folder picker attributes not recognized by React's typed props
  useEffect(() => {
    if (folderRef.current) {
      folderRef.current.setAttribute("webkitdirectory", "");
      folderRef.current.setAttribute("directory", "");
    }
  }, [mode]);

  // Shared metadata
  const [categoryId, setCategoryId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [deps, setDeps] = useState([]);
  const [tags, setTags] = useState("");
  // expiryDate is now a Date object (or "")
  const [expiryDate, setExpiryDate] = useState("");
  const [description, setDescription] = useState("");

  // Single-file state
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [revisionNotes, setRevisionNotes] = useState("");

  // Bulk state — [{ id, file, title, status: 'queued'|'uploading'|'done'|'error', error? }]
  const [queue, setQueue] = useState([]);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [aiClassify, setAiClassify] = useState(true);
  const [classifying, setClassifying] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/departments").then((r) => {
      const all = r.data;
      const allowed = user?.role === "operator"
        ? all.filter((d) => (user.department_ids || []).includes(d.id))
        : all;
      setDeps(allowed);
      if (preselectedDept) {
        if (allowed.some((d) => d.id === preselectedDept)) {
          setDepartmentId(preselectedDept);
        } else if (user?.role === "operator") {
          toast.info("You don't have access to that department yet — pick from your allowed list.");
        }
      }
    });
  }, [user, preselectedDept]);

  const summary = useMemo(() => {
    const done = queue.filter((q) => q.status === "done").length;
    const err = queue.filter((q) => q.status === "error").length;
    return { done, err, total: queue.length };
  }, [queue]);

  const addFiles = (files) => {
    const arr = Array.from(files).filter(Boolean);
    const newItems = arr.map((f) => ({
      id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      title: stripExt(f.name),
      status: "queued",
      suggested_department_id: "",
      suggested_department_name: "",
      confidence: "",
      classify_reason: "",
      duplicates: [],
      hash: "",
    }));
    setQueue((prev) => [...prev, ...newItems]);
    // Fire duplicate check for each new file (fail-silent, non-blocking)
    newItems.forEach((it) => { checkDuplicate(it); });
  };

  const classifyOne = async (item) => {
    const fd = new FormData();
    fd.append("file", item.file);
    try {
      const { data } = await api.post("/documents/classify", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setQueue((prev) => prev.map((q) => q.id === item.id ? {
        ...q,
        suggested_department_id: data.department_id,
        suggested_department_name: data.department_name,
        confidence: data.confidence,
        classify_reason: data.reason,
      } : q));
    } catch (err) {
      setQueue((prev) => prev.map((q) => q.id === item.id ? {
        ...q, classify_reason: `Auto-classify failed: ${formatApiError(err.response?.data?.detail) || err.message}`,
      } : q));
    }
  };

  const checkDuplicate = async (item) => {
    const fd = new FormData();
    fd.append("file", item.file);
    try {
      const { data } = await api.post("/documents/check-duplicate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setQueue((prev) => prev.map((q) => q.id === item.id ? {
        ...q,
        duplicates: data.duplicates || [],
        hash: data.hash,
      } : q));
    } catch (err) {
      // Fail silently — duplicate check is a nice-to-have; don't block upload
    }
  };

  const classifyAll = async () => {
    if (!aiClassify) return;
    setClassifying(true);
    // Sequential to keep LLM rate-limit friendly
    for (const item of queue) {
      if (!item.suggested_department_id && item.status !== "done") {
        await classifyOne(item);
      }
    }
    setClassifying(false);
    toast.success("AI classification complete");
  };

  const onDropSingle = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const onDropBulk = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const submitSingle = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please select a file");
    if (!departmentId) return toast.error("Please choose a department");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("description", description);
      if (categoryId) fd.append("category_id", categoryId);
      fd.append("department_id", departmentId);
      fd.append("tags", tags);
      if (revisionNotes.trim()) fd.append("revision_notes", revisionNotes.trim());
      if (expiryDate) fd.append("expiry_date", new Date(expiryDate).toISOString());
      const { data } = await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded — awaiting approval");
      nav(`/documents/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const runBulk = async () => {
    if (queue.length === 0) return toast.error("Add at least one file");
    setBulkRunning(true);
    for (const item of queue) {
      if (item.status === "done") continue;
      // Effective dept: user-picked override wins, else per-file AI suggestion, else batch dept
      const effectiveDept = item.suggested_department_id || departmentId;
      if (!effectiveDept) {
        setQueue((prev) => prev.map((q) => (q.id === item.id ? { ...q, status: "error", error: "No department — pick a batch dept or enable AI classify" } : q)));
        continue;
      }
      setQueue((prev) => prev.map((q) => (q.id === item.id ? { ...q, status: "uploading" } : q)));
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("title", item.title || stripExt(item.file.name));
        fd.append("description", description);
        if (categoryId) fd.append("category_id", categoryId);
        fd.append("department_id", effectiveDept);
        fd.append("tags", tags);
        if (expiryDate) fd.append("expiry_date", new Date(expiryDate).toISOString());
        await api.post("/documents/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setQueue((prev) => prev.map((q) => (q.id === item.id ? { ...q, status: "done" } : q)));
      } catch (err) {
        const msg = formatApiError(err.response?.data?.detail) || err.message;
        setQueue((prev) => prev.map((q) => (q.id === item.id ? { ...q, status: "error", error: msg } : q)));
      }
    }
    setBulkRunning(false);
    toast.success("Bulk upload finished");
  };

  const removeFromQueue = (id) =>
    setQueue((prev) => prev.filter((q) => q.id !== id));

  const clearDone = () =>
    setQueue((prev) => prev.filter((q) => q.status !== "done"));

  return (
    <div className="max-w-4xl" data-testid="upload-page">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="label-caps">New documents</div>
          <h2 className="font-display text-3xl text-navy uppercase mt-1">
            {mode === "single" ? "Upload document" : "Bulk upload"}
          </h2>
          <p className="text-sm text-slate-500 mt-2">
            {mode === "single"
              ? "Add one file with full metadata — the classic single-file flow."
              : "Drop a whole folder of SOPs or drawings. Shared metadata is applied to every file. Titles default to filenames and can be edited inline."}
          </p>
        </div>
        <div className="inline-flex border border-navy rounded-sm overflow-hidden" data-testid="upload-mode-toggle">
          <button
            type="button"
            data-testid="mode-single"
            onClick={() => setMode("single")}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold transition-colors ${mode === "single" ? "bg-navy text-white" : "bg-white text-navy hover:bg-slate-100"}`}
          >
            Single
          </button>
          <button
            type="button"
            data-testid="mode-bulk"
            onClick={() => setMode("bulk")}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold transition-colors ${mode === "bulk" ? "bg-navy text-white" : "bg-white text-navy hover:bg-slate-100"}`}
          >
            Bulk
          </button>
        </div>
      </div>

      {/* Shared metadata card */}
      <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="md:col-span-2 label-caps">Shared metadata</div>
        <div className="space-y-1.5">
          <Label>Department <span className="text-red-500">*</span></Label>
          <Select value={departmentId} onValueChange={setDepartmentId}>
            <SelectTrigger data-testid="doc-department-select"><SelectValue placeholder={deps.length === 0 ? "No departments assigned to you" : "Choose department"} /></SelectTrigger>
            <SelectContent>
              {deps.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {user?.role === "operator" && deps.length === 0 && (
            <p className="text-xs text-red-600">You&apos;re not assigned to any department yet. Ask an admin to grant access on the Users page.</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Category</Label>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger data-testid="doc-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
            <SelectContent>
              {cats.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="expiry">Expiry date (optional)</Label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                id="expiry"
                type="button"
                data-testid="doc-expiry-input"
                className="flex items-center justify-between gap-2 w-full h-10 px-3 text-sm border border-slate-200 rounded-sm bg-white hover:border-cyan transition-colors"
              >
                <span className={expiryDate ? "text-zinc-900" : "text-slate-400"}>
                  {expiryDate ? format(expiryDate, "PPP") : "Pick expiry date"}
                </span>
                <CalendarBlank size={16} className="text-cyan" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-auto" align="start">
              <Calendar
                mode="single"
                selected={expiryDate || undefined}
                onSelect={(d) => setExpiryDate(d || "")}
                initialFocus
              />
              {expiryDate && (
                <div className="border-t px-3 py-2 flex items-center justify-between">
                  <span className="text-xs font-mono text-slate-500">{format(expiryDate, "yyyy-MM-dd")}</span>
                  <button
                    type="button"
                    data-testid="expiry-clear"
                    onClick={() => setExpiryDate("")}
                    className="text-xs text-slate-500 hover:text-navy"
                  >
                    Clear
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>
        <div className="md:col-span-2 space-y-1.5">
          <Label htmlFor="tags">Tags (comma-separated)</Label>
          <Input
            id="tags"
            data-testid="doc-tags-input"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="iso-9001, molding, line-a"
          />
        </div>
        <div className="md:col-span-2 space-y-1.5">
          <Label htmlFor="desc">Description (applied to all)</Label>
          <Textarea
            id="desc"
            data-testid="doc-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Short summary — will help teams find these documents."
          />
        </div>
      </div>

      {mode === "single" && (
        <form onSubmit={submitSingle} className="card-flat p-6 space-y-5">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDropSingle}
            className="border-2 border-dashed border-slate-300 hover:border-cyan transition-colors rounded-sm p-8 text-center bg-slate-50/50"
            data-testid="upload-dropzone"
          >
            <FileArrowUp size={32} className="mx-auto text-slate-400 mb-2" />
            <div className="text-sm text-slate-600">
              {file ? <span className="font-mono text-navy">{file.name}</span> : "Drop a PDF, image or drawing here"}
            </div>
            <label className="inline-block mt-3">
              <input
                type="file"
                className="hidden"
                data-testid="file-input"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.dwg,.dxf,.docx,.xlsx,.pptx"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <span className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 rounded-sm bg-white hover:bg-slate-100 transition-colors">
                <UploadSimple size={14} /> Choose file
              </span>
            </label>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              data-testid="doc-title-input"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="SOP-014 · Injection molding startup"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="initial-notes">
              Initial context <span className="text-xs text-slate-500 font-normal">(optional)</span>
            </Label>
            <Textarea
              id="initial-notes"
              data-testid="initial-notes-input"
              value={revisionNotes}
              onChange={(e) => setRevisionNotes(e.target.value)}
              rows={2}
              placeholder="What is this document for? Why is it being added now?"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => nav(-1)} className="rounded-sm">
              Cancel
            </Button>
            <button type="submit" disabled={submitting} data-testid="submit-upload" className="btn-navy">
              {submitting ? "Uploading…" : "Upload & submit →"}
            </button>
          </div>
        </form>
      )}

      {mode === "bulk" && (
        <div className="card-flat p-6 space-y-5">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDropBulk}
            className="border-2 border-dashed border-slate-300 hover:border-cyan transition-colors rounded-sm p-8 text-center bg-slate-50/50"
            data-testid="bulk-dropzone"
          >
            <Folder size={32} className="mx-auto text-slate-400 mb-2" weight="duotone" />
            <div className="text-sm text-slate-600">Drop multiple files or a whole folder here</div>
            <div className="mt-4 flex items-center justify-center gap-3">
              <label className="inline-block">
                <input
                  type="file"
                  className="hidden"
                  multiple
                  data-testid="bulk-file-input"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.dwg,.dxf,.docx,.xlsx,.pptx"
                  onChange={(e) => e.target.files && addFiles(e.target.files)}
                />
                <span className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 rounded-sm bg-white hover:bg-slate-100 transition-colors">
                  <UploadSimple size={14} /> Choose files
                </span>
              </label>
              <button
                type="button"
                data-testid="pick-folder"
                onClick={() => folderRef.current?.click()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 rounded-sm bg-white hover:bg-slate-100 transition-colors"
              >
                <Folder size={14} /> Pick folder
              </button>
              <input
                type="file"
                ref={folderRef}
                className="hidden"
                data-testid="folder-input"
                multiple
                onChange={(e) => e.target.files && addFiles(e.target.files)}
              />
            </div>
          </div>

          {queue.length > 0 && (
            <>
              <div className="flex items-center justify-between text-sm">
                <div className="text-slate-600">
                  <span className="font-semibold text-navy" data-testid="queue-summary">
                    {summary.done}/{summary.total} uploaded
                  </span>
                  {summary.err > 0 && (
                    <span className="ml-2 text-red-600">· {summary.err} failed</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={aiClassify}
                      onChange={(e) => setAiClassify(e.target.checked)}
                      data-testid="ai-classify-toggle"
                      className="rounded-sm"
                    />
                    <Sparkle size={12} weight="fill" className="text-cyan" />
                    AI classify to department
                  </label>
                  {aiClassify && (
                    <button
                      type="button"
                      onClick={classifyAll}
                      disabled={classifying || bulkRunning}
                      data-testid="run-classify"
                      className="text-xs uppercase tracking-widest px-3 py-1.5 border border-cyan text-navy rounded-sm hover:bg-cyan/10 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {classifying ? <Spinner size={11} className="animate-spin" /> : <Sparkle size={11} weight="fill" />}
                      {classifying ? "Classifying…" : "Run AI classify"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={clearDone}
                    className="text-xs uppercase tracking-widest text-slate-500 hover:text-navy transition-colors"
                  >
                    Clear completed
                  </button>
                </div>
              </div>

              <div className="border border-slate-200 rounded-sm divide-y divide-slate-200 max-h-[420px] overflow-auto" data-testid="bulk-queue">
                {queue.map((q) => (
                  <div key={q.id} className="p-3 flex items-center gap-3" data-testid={`queue-item-${q.id}`}>
                    <StatusIcon status={q.status} />
                    <div className="flex-1 min-w-0">
                      <Input
                        value={q.title}
                        onChange={(e) =>
                          setQueue((prev) =>
                            prev.map((x) => (x.id === q.id ? { ...x, title: e.target.value } : x))
                          )
                        }
                        disabled={q.status === "uploading" || q.status === "done"}
                        className="h-8 text-sm"
                        data-testid={`queue-title-${q.id}`}
                      />
                      <div className="mt-1 flex items-center gap-2 text-[11px] font-mono text-slate-500 flex-wrap">
                        <span className="truncate">{q.file.name}</span>
                        <span>·</span>
                        <span>{(q.file.size / 1024).toFixed(1)} KB</span>
                        {q.status === "error" && (
                          <span className="text-red-600 truncate">· {q.error}</span>
                        )}
                        {q.classify_reason && !q.suggested_department_id && (
                          <span className="text-amber-600 truncate">· {q.classify_reason}</span>
                        )}
                      </div>
                      {q.duplicates?.length > 0 && (
                        <div
                          data-testid={`duplicate-warn-${q.id}`}
                          className="mt-2 text-[11px] bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 flex items-start gap-2"
                        >
                          <Warning size={13} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-amber-800 font-semibold">
                              Duplicate content detected ({q.duplicates.length} match{q.duplicates.length === 1 ? "" : "es"})
                            </div>
                            <div className="text-slate-600 mt-0.5 truncate">
                              Matches: {q.duplicates.map((d) => `${d.title} (v${d.match_version || d.current_version})`).join(", ")}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="shrink-0">
                      {q.suggested_department_id ? (
                        <Select
                          value={q.suggested_department_id}
                          onValueChange={(v) =>
                            setQueue((prev) => prev.map((x) => (x.id === q.id ? {
                              ...x, suggested_department_id: v, suggested_department_name: deps.find((d) => d.id === v)?.name || "",
                            } : x)))
                          }
                        >
                          <SelectTrigger
                            data-testid={`queue-dept-${q.id}`}
                            className={`h-8 text-xs w-56 ${
                              q.confidence === "high" ? "border-emerald-300" :
                              q.confidence === "low" ? "border-amber-300" : ""
                            }`}
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Sparkle size={10} weight="fill" className={
                                q.confidence === "high" ? "text-emerald-500" :
                                q.confidence === "low" ? "text-amber-500" : "text-cyan"
                              } />
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            {deps.map((d) => (
                              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
                          <Buildings size={10} /> {departmentId ? "Batch dept" : "No dept yet"}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFromQueue(q.id)}
                      disabled={bulkRunning}
                      className="text-slate-400 hover:text-red-600 transition-colors disabled:opacity-30"
                      aria-label="Remove"
                      data-testid={`remove-${q.id}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => setQueue([])} disabled={bulkRunning} className="rounded-sm">
              Reset
            </Button>
            <button
              type="button"
              onClick={runBulk}
              disabled={bulkRunning || queue.length === 0}
              data-testid="submit-bulk"
              className="btn-navy"
            >
              {bulkRunning ? "Uploading…" : `Upload ${queue.length || ""} file${queue.length === 1 ? "" : "s"} →`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "done")
    return <CheckCircle size={20} weight="fill" className="text-emerald-500 shrink-0" />;
  if (status === "error")
    return <Warning size={20} weight="fill" className="text-red-500 shrink-0" />;
  if (status === "uploading")
    return <Spinner size={20} className="text-navy animate-spin shrink-0" />;
  return <div className="w-5 h-5 rounded-sm border border-slate-300 shrink-0" />;
}
